import { inject, Injectable, PLATFORM_ID, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, tap, throwError } from 'rxjs';
import { ConfigService } from '../config/config.service';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  refreshExpiresAt: number;
}

const STORAGE_KEY = 'auth_session';
const FORM_HEADERS = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

/**
 * Magazyn sesji procesu głównego (tylko build Electrona), zaszyfrowany kluczem
 * systemowym. Wariant webowy nie ma odpowiednika i zostaje przy localStorage.
 */
interface SessionStoreBridge {
  loadSession?(): Promise<AuthTokens | null>;
  saveSession?(session: AuthTokens | null): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject(ConfigService);

  private readonly _tokens = signal<AuthTokens | null>(this.loadFromStorage());

  private get bridge(): SessionStoreBridge | null {
    const w = globalThis as unknown as { posBridge?: { store?: SessionStoreBridge } };
    const store = w.posBridge?.store ?? null;
    return store?.saveSession ? store : null;
  }

  readonly isAuthenticated = computed(() => {
    const tokens = this._tokens();
    return !!tokens && tokens.expiresAt > Date.now();
  });

  readonly accessToken = computed(() => this._tokens()?.accessToken ?? null);

  readonly sessionExpiresAt = computed(() => this._tokens()?.refreshExpiresAt ?? null);

  readonly accessTokenExpiresAt = computed(() => this._tokens()?.expiresAt ?? null);

  login(username: string, password: string): Observable<void> {
    const kc = this.config.keycloak();
    const body = new URLSearchParams({
      grant_type: 'password',
      client_id: kc.clientId,
      username,
      password,
      scope: 'openid',
    });

    return this.http
      .post<KeycloakTokenResponse>(this.tokenUrl(), body.toString(), { headers: FORM_HEADERS })
      .pipe(
        tap((res) => this.storeTokens(res)),
        map(() => void 0),
      );
  }

  refreshAccessToken(): Observable<void> {
    const tokens = this._tokens();
    if (!tokens) return throwError(() => new Error('No session'));

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.config.keycloak().clientId,
      refresh_token: tokens.refreshToken,
    });

    return this.http
      .post<KeycloakTokenResponse>(this.tokenUrl(), body.toString(), { headers: FORM_HEADERS })
      .pipe(
        tap((res) => this.storeTokens(res)),
        map(() => void 0),
      );
  }

  logout(): void {
    const tokens = this._tokens();
    if (tokens) {
      const body = new URLSearchParams({
        client_id: this.config.keycloak().clientId,
        refresh_token: tokens.refreshToken,
      });
      this.http
        .post(this.logoutUrl(), body.toString(), { headers: FORM_HEADERS })
        .subscribe({ error: () => {} });
    }

    this._tokens.set(null);
    this.persistSession(null);
    this.router.navigate(['/auth/login']);
  }

  forgotPasswordUrl(redirectUri: string): string {
    const kc = this.config.keycloak();
    const params = new URLSearchParams({
      client_id: kc.clientId,
      redirect_uri: redirectUri,
    });
    return `${kc.url}/realms/${kc.realm}/login-actions/reset-credentials?${params}`;
  }

  private tokenUrl(): string {
    const kc = this.config.keycloak();
    return `${kc.url}/realms/${kc.realm}/protocol/openid-connect/token`;
  }

  private logoutUrl(): string {
    const kc = this.config.keycloak();
    return `${kc.url}/realms/${kc.realm}/protocol/openid-connect/logout`;
  }

  private storeTokens(res: KeycloakTokenResponse): void {
    const tokens: AuthTokens = {
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
      expiresAt: Date.now() + res.expires_in * 1000,
      refreshExpiresAt: Date.now() + res.refresh_expires_in * 1000,
    };
    this._tokens.set(tokens);
    this.persistSession(tokens);
  }

  /**
   * Zapis sesji: pod Electronem do zaszyfrowanego magazynu procesu głównego,
   * w przeglądarce — jak dotąd — do localStorage. Zapis przez mostek jest
   * asynchroniczny, a wołamy go z miejsc synchronicznych, więc jego błąd nie może
   * przewrócić logowania ani wylogowania.
   */
  private persistSession(tokens: AuthTokens | null): void {
    const bridge = this.bridge;
    if (bridge?.saveSession) {
      void bridge
        .saveSession(tokens)
        .catch((e: unknown) => console.warn('Sesja kasy: nie udało się jej zapisać', e));
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (tokens) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Odtwarza sesję z magazynu procesu głównego. Odczyt przez mostek jest
   * asynchroniczny, więc nie da się go zrobić w konstruktorze — woła to
   * inicjalizator aplikacji (`app.config.ts`), który kończy się przed pierwszą
   * nawigacją routera. Inaczej `authGuard` zobaczyłby pustą sesję i kasa wracałaby
   * na ekran logowania przy każdym starcie.
   */
  async restoreSession(): Promise<void> {
    const bridge = this.bridge;
    if (!bridge?.loadSession) {
      return;
    }
    try {
      const tokens = await bridge.loadSession();
      if (!tokens) {
        return;
      }
      if (tokens.refreshExpiresAt <= Date.now()) {
        await bridge.saveSession?.(null);
        return;
      }
      this._tokens.set(tokens);
    } catch (e) {
      console.warn('Sesja kasy: nie udało się jej odczytać', e);
    }
  }

  private loadFromStorage(): AuthTokens | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      if (this.bridge) {
        // Ślad po wcześniejszych wersjach kasy: sesja bywała zapisana jawnie w profilu.
        // Kasujemy ją przy pierwszym starcie z magazynem szyfrowanym, żeby token
        // odświeżenia nie został na dysku aż do wygaśnięcia sesji SSO.
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const tokens = JSON.parse(raw) as AuthTokens;
      if (tokens.refreshExpiresAt <= Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return tokens;
    } catch {
      return null;
    }
  }
}

interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}
